"use client";

import { AmplitudeAnalytics } from "@wb/ui/analytics/amplitude/AmplitudeAnalytics";
import { CloudflareAnalytics } from "@wb/ui/analytics/cf/CloudflareAnalytics";
import { ClarityAnalytics } from "@wb/ui/analytics/clarity/ClarityAnalytics";
import { GoogleAnalytics } from "@wb/ui/analytics/ga/GoogleAnalytics";
import { HOST, host } from "@wb/ui/analytics/host";
import { PostHogAnalytics } from "@wb/ui/analytics/posthog/PostHogAnalytics";
import { state, stateSchema } from "@wb/ui/analytics/state";
import { useTrack } from "@wb/ui/analytics/useTrack";
import { VercelAnalytics } from "@wb/ui/analytics/vercel/VercelAnalytics";
import type { FC } from "react";
import type { z } from "zod";

const schema = stateSchema.extend({
  host,
});

type Props = z.infer<typeof schema>;

/**
 * Analytics component
 *
 * @example
 *
 * ```tsx
 * <Analytics
 *  host="example.com"
 *  enabled
 *  ga
 *  clarity
 *  posthog
 *  vercel
 *  cf
 *  amplitude
 * />
 * ```
 *
 * ```tsx
 * <button data-event="button_click">
 *  Click me
 * </button>
 * ```
 *
 */
export const Analytics: FC<Props> = (props) => {
  "use no memo";

  const {
    ga,
    clarity,
    posthog,
    vercel,
    cf,
    amplitude,
    host,
    enabled = true,
    debug = false,
  } = schema.parse(props);

  state.host = host;
  state.enabled = enabled;
  state.debug = debug;
  state.ga = ga;
  state.clarity = clarity;
  state.posthog = posthog;
  state.vercel = vercel;
  state.cf = cf;
  state.amplitude = amplitude;

  useTrack();

  if (!enabled) return null;

  return (
    <>
      {ga && HOST[host].ga && <GoogleAnalytics id={HOST[host].ga} />}
      {clarity && HOST[host].clarity && (
        <ClarityAnalytics id={HOST[host].clarity} />
      )}
      {posthog && HOST[host].posthog && (
        <PostHogAnalytics id={HOST[host].posthog} />
      )}
      {cf && HOST[host].cf && <CloudflareAnalytics id={HOST[host].cf} />}
      {vercel && <VercelAnalytics />}
      {amplitude && <AmplitudeAnalytics id={HOST[host].amplitude} />}
    </>
  );
};
